import { request } from '@/utils';
import { useQuery } from 'react-query';
import { HiUserInfo, HiMessagePermission } from '../types';
import produce from 'immer';
import { useCallback, useMemo } from 'react';
import { atom, useRecoilState, useSetRecoilState } from 'recoil';
import { useCurrentAddressInfo } from '@/hooks';

export enum MessagePermissionLevel {
  free = 0,
  restricted = 1,
  needPay = 2,
  forbidden = 3,
}

export const useHiMessagePermission = (
  toUserId: HiUserInfo['id'] = '',
  addRefetchInterval: boolean = false,
) => {
  const { address: fromUserId } = useCurrentAddressInfo();
  const putHiMessagePermission = usePutHiMessagePermission();
  const [permissionMap] = useRecoilState(HiMessagePermissionStore);
  const key = useMemo(() => fromUserId + '-' + toUserId, [fromUserId, toUserId]);

  const { isLoading, refetch } = useQuery(
    ['/hi/user_message_permission', toUserId, fromUserId],
    async () => {
      return await request
        .get<HiMessagePermission>('/hi/user_message_permission', {
          to_user_id: toUserId,
        })
        .then(permission => {
          permission && putHiMessagePermission(permission);
        });
    },
    {
      refetchInterval: addRefetchInterval ? 10000 : undefined,
      enabled: !!fromUserId && !!toUserId,
    },
  );

  return {
    isLoading,
    permission: permissionMap[key],
    refetch,
  };
};

type HiMessagePermissionMap = Record<string, HiMessagePermission>;

const HiMessagePermissionStore = atom<HiMessagePermissionMap>({
  key: 'HiMessagePermission',
  default: {},
});

export const usePutHiMessagePermission = () => {
  const set = useSetRecoilState(HiMessagePermissionStore);
  const putHiMessagePermission = useCallback(
    (permission: HiMessagePermission) => {
      if (!permission.from_user_id || !permission.to_user_id) return;
      set(permissionMap => {
        const key = permission.from_user_id + '-' + permission.to_user_id;
        const nextState = produce(permissionMap, draft => {
          draft[key] = permission;
        });
        return nextState;
      });
    },
    [set],
  );
  return putHiMessagePermission;
};
